import { Alert, Box, Button, Grid2 as Grid, Typography } from "@mui/material";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import { CenteredCircularLoading } from "@/components/common/CenteredCircularLoading";
import { MonthCell } from "@/resources/policies/components/MonthlyEnrollmentSection";
import type { AccommodationEnrollmentGate as Gate } from "./useAccommodationEnrollmentGate";

/**
 * Monthly enrollment grid inside the accommodation run dialogs. Shows the
 * coverage months the run reads, flags the ones missing enrolled lives, and offers
 * "Copy prior month" to fill every gap. Entries are saved with the run, not here.
 */
export const AccommodationEnrollmentGate = ({ gate }: { gate: Gate }) => {
  const { months, livesByMonth, copiedMonths, missing, isFlatAttachment, isLoading, isError } = gate;
  if (months.length === 0) return null;

  const missingSet = new Set(missing.map((m) => m.coverageMonth));
  const range = months.length > 1 ? `${months[0].label} – ${months[months.length - 1].label}` : months[0].label;

  return (
    <Box>
      <Box sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Monthly Enrollment</Typography>
        <Typography variant="caption" color="text.secondary">
          {range}
        </Typography>
      </Box>
      {isFlatAttachment && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Not used: flat annual attachment in effect.
        </Typography>
      )}
      {isError ? (
        <Alert severity="error">Failed to load monthly enrollment. Please refresh and try again.</Alert>
      ) : isLoading ? (
        <CenteredCircularLoading size={32} />
      ) : (
        <Box sx={{ maxHeight: 260, overflowY: "auto", pr: 0.5 }}>
          <Grid container spacing={1}>
            {months.map((m) => (
              <Grid key={m.coverageMonth} size={4}>
                <MonthCell
                  label={m.label}
                  isPartial={m.isPartial}
                  value={livesByMonth.get(m.coverageMonth)}
                  readOnly={isFlatAttachment}
                  onSave={(lives) => gate.setLives(m.coverageMonth, lives)}
                  isMissing={missingSet.has(m.coverageMonth)}
                  isCopied={copiedMonths.has(m.coverageMonth)}
                  onInvalidChange={(invalid) => gate.setInvalid(m.coverageMonth, invalid)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      {!isFlatAttachment && !isError && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
          <Button
            size="small"
            startIcon={<ContentCopyOutlined />}
            onClick={gate.copyPriorMonth}
            disabled={isLoading || !gate.canCopyPriorMonth}
            sx={{ whiteSpace: "nowrap" }}
          >
            Copy prior month
          </Button>
        </Box>
      )}
      {!isFlatAttachment && !isLoading && !isError && missing.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {missing.length === 1 ? "1 month needs" : `${missing.length} months need`} enrolled lives before this can run:{" "}
          {missing.map((m) => m.label).join(", ")}
        </Alert>
      )}
    </Box>
  );
};
